import type { Material, Stock } from "./plans"

export type PlannedTask = {
  name: string
  ref: string
  after?: string[]
  parts?: Material[]
  tools?: Material[]
}

export type PlannedStep = {
  name: string
  days: number
  offset: number
  shop: string
  after: string[]
  tasks: PlannedTask[]
}

function item(
  number: string,
  name: string,
  stock: Stock,
  state: string,
): Material {
  return { number, name, stock, state }
}

const APU = "APU servicing"
const BATTERIES = "Battery capacity checks"
const CABIN_SAFETY = "Cabin safety equipment"
const ELT = "ELT inspections"
const DOCUMENT_1 = "Document 1 annual inspection"
const ENGINE_1 = "No. 1 engine periodic inspections"
const ENGINE_2 = "No. 2 engine periodic inspections"
const FLAP_DOOR = "Enhanced flap and door maintenance programs"
const DOCUMENTS_3_TO_11 = "Inspection documents 3, 6, 7 and 11"
const DOCUMENTS_16_19 = "Inspection documents 16 and 19"
const NDT = "NDT inspection document MN"
const WINDSHIELD = "Windshield re-seal inspection"
const PITOT_STATIC = "Pitot-static and RVSM certification"
const TRANSPONDERS = "Transponder certification"
const COMPASS_SWING = "Compass swing"

const AIR_DATA_TEST_SET = item(
  "ADTS-505",
  "Air data test set",
  "Available",
  "Calibration due Nov 12",
)

const BORESCOPE_KIT = item("BSK-6", "Borescope kit", "Available", "Tool crib")

const ENGINE_OIL_FILTER = item(
  "EOF-545",
  "Engine oil filter element",
  "Low",
  "2 left · reorder raised",
)

export const WO20_STEPS: PlannedStep[] = [
  {
    name: APU,
    days: 3,
    offset: 0,
    shop: "Powerplant",
    after: [],
    tasks: [
      { name: "Inspect the APU chip detector", ref: "WO-20.1" },
      {
        name: "Service the APU oil and change the filter",
        ref: "WO-20.10",
        after: ["WO-20.1"],
        parts: [
          item(
            "MIL-PRF-23699",
            "Turbine oil, 1 qt (×4)",
            "Available",
            "Reserved",
          ),
          item(
            "APF-1007",
            "APU oil filter element",
            "Out of stock",
            "On order · ETA Nov 3",
          ),
        ],
      },
      {
        name: "Replace the APU fuel filter",
        ref: "WO-20.22",
        after: ["WO-20.10"],
        parts: [
          item("APF-2210", "APU fuel filter element", "Available", "Reserved"),
        ],
      },
    ],
  },
  {
    name: BATTERIES,
    days: 2,
    offset: 0,
    shop: "Shop",
    after: [],
    tasks: [
      {
        name: "Check main battery capacity",
        ref: "WO-20.2",
        tools: [item("BA-24", "Battery analyser", "Available", "Battery shop")],
      },
      {
        name: "Perform the No. 2 standby battery capacity check",
        ref: "WO-20.8",
      },
      { name: "Check main ship battery capacity", ref: "WO-20.25" },
      {
        name: "Check the CVR underwater locator beacon battery time",
        ref: "WO-20.24",
      },
    ],
  },
  {
    name: CABIN_SAFETY,
    days: 1,
    offset: 0,
    shop: "Interiors",
    after: [],
    tasks: [
      { name: "Inspect the first aid kit contents", ref: "WO-20.3" },
      {
        name: "Service the first aid kit",
        ref: "WO-20.32",
        parts: [
          item("FAK-12-R", "First aid kit refill", "Available", "Stores"),
        ],
      },
      {
        name: "Perform a hydro test on the nitrogen storage bottle when its time expires",
        ref: "WO-20.28",
      },
    ],
  },
  {
    name: ELT,
    days: 1,
    offset: 0,
    shop: "Avionics",
    after: [],
    tasks: [
      {
        name: "Perform 25-60-10-7202 ELT inspection",
        ref: "WO-20.23",
        tools: [item("ELT-TS-2", "ELT test set", "Available", "Avionics bay")],
      },
      { name: "Emergency locator transmitter inspection", ref: "WO-20.30" },
    ],
  },
  {
    name: DOCUMENT_1,
    days: 5,
    offset: 0,
    shop: "Structures",
    after: [],
    tasks: [
      { name: "Inspect 05-12-01 annual inspection items", ref: "WO-20.9" },
      {
        name: "Perform 05-12-01 inspection document 1 requirements",
        ref: "WO-20.11",
      },
      { name: "Perform the 05-12-01 program item", ref: "WO-20.12" },
      { name: "Perform inspection document 1", ref: "WO-20.20" },
      {
        name: "Perform the 05-12-01 annual inspection requirements",
        ref: "WO-20.36",
      },
      { name: "Perform the 05-12-01 inspection package", ref: "WO-20.37" },
      {
        name: "Complete 12 month airframe items due under document 1",
        ref: "WO-20.45",
      },
    ],
  },
  {
    name: ENGINE_1,
    days: 3,
    offset: 3,
    shop: "Powerplant",
    after: [],
    tasks: [
      {
        name: "050000 No. 1 engine 800 hour periodic inspection",
        ref: "WO-20.41",
        parts: [ENGINE_OIL_FILTER],
        tools: [BORESCOPE_KIT],
      },
      {
        name: "Perform the 057120 No. 1 engine low utilization inspection",
        ref: "WO-20.16",
      },
      { name: "Left engine minor inspection", ref: "WO-20.34" },
    ],
  },
  {
    name: ENGINE_2,
    days: 3,
    offset: 6,
    shop: "Powerplant",
    after: [],
    tasks: [
      {
        name: "Perform the 050000 No. 2 engine 800 hour periodic inspection",
        ref: "WO-20.15",
        parts: [ENGINE_OIL_FILTER],
        tools: [BORESCOPE_KIT],
      },
      {
        name: "Perform 057120 No. 2 engine low utilization inspection",
        ref: "WO-20.44",
      },
      { name: "Right engine minor inspection", ref: "WO-20.33" },
    ],
  },
  {
    name: FLAP_DOOR,
    days: 2,
    offset: 2,
    shop: "Shop",
    after: [],
    tasks: [
      {
        name: "Perform the enhanced flap maintenance program",
        ref: "WO-20.7",
        parts: [
          item(
            "MIL-PRF-81322",
            "Grease, aircraft, 14 oz (×2)",
            "Available",
            "Reserved",
          ),
        ],
        tools: [item("DI-05", "Dial indicator set", "Available", "Tool crib")],
      },
      {
        name: "Perform the enhanced door maintenance program",
        ref: "WO-20.31",
      },
    ],
  },
  {
    name: DOCUMENTS_3_TO_11,
    days: 3,
    offset: 5,
    shop: "Structures",
    after: [],
    tasks: [
      { name: "Perform the scheduled 5-12-11 requirement", ref: "WO-20.4" },
      {
        name: "Accomplish 05-12-11 inspection document 11",
        ref: "WO-20.14",
      },
      { name: "Perform the 05-12-03 scheduled items", ref: "WO-20.13" },
      { name: "Perform the 24 month 5-12-03 items", ref: "WO-20.17" },
      { name: "Perform the 48 month 5-12-06 items", ref: "WO-20.5" },
      { name: "Perform the 05-12-06 recurring items", ref: "WO-20.38" },
      { name: "05-12-07 recurring inspection", ref: "WO-20.39" },
    ],
  },
  {
    name: DOCUMENTS_16_19,
    days: 2,
    offset: 8,
    shop: "Structures",
    after: [],
    tasks: [
      { name: "Perform inspection document 16", ref: "WO-20.21" },
      {
        name: "Perform the 05-12-19 scheduled inspection",
        ref: "WO-20.27",
      },
      {
        name: "Accomplish the 05-12-19 300 hour inspection document 19",
        ref: "WO-20.40",
      },
    ],
  },
  {
    name: NDT,
    days: 1,
    offset: 10,
    shop: "Structures",
    after: [],
    tasks: [
      {
        name: "Perform the 04-12-MN NDT inspection document MN",
        ref: "WO-20.35",
        tools: [item("ECT-30", "Eddy current tester", "Reserved", "NDT shop")],
      },
    ],
  },
  {
    name: WINDSHIELD,
    days: 1,
    offset: 11,
    shop: "Structures",
    after: [],
    tasks: [
      {
        name: "Perform 055601 windshield surface re-seal inspection",
        ref: "WO-20.43",
        parts: [item("PS-1425", "Windshield sealant", "Low", "Reserved")],
      },
    ],
  },
  {
    name: PITOT_STATIC,
    days: 2,
    offset: 10,
    shop: "Avionics",
    after: [],
    tasks: [
      {
        name: "Certify the pilot pitot-static system",
        ref: "WO-20.6",
        tools: [AIR_DATA_TEST_SET],
      },
      {
        name: "Perform the 24 month FAR 91.411 / 91.413 inspection",
        ref: "WO-20.18",
      },
      {
        name: "Perform the 24 month FAR 91.411 and 91.413 RVSM inspection",
        ref: "WO-20.19",
      },
      {
        name: "Perform the 24 month RVSM and transponder checks",
        ref: "WO-20.29",
      },
      {
        name: "Perform 24 month 91.411/91.413 and RVSM checks",
        ref: "WO-20.48",
      },
      {
        name: "Perform the 24 month RVSM and transponder interval maintenance",
        ref: "WO-20.49",
      },
      {
        name: "Perform 24 month RVSM, pitot-static, and transponder checks",
        ref: "WO-20.50",
        tools: [AIR_DATA_TEST_SET],
      },
    ],
  },
  {
    name: TRANSPONDERS,
    days: 1,
    offset: 12,
    shop: "Avionics",
    after: [],
    tasks: [
      { name: "Certify the No. 2 transponder", ref: "WO-20.26" },
      {
        name: "Perform 23-90-20-7200 transponder certification on No.1 and No.2 GTX 3000 units",
        ref: "WO-20.46",
        tools: [
          item("IFR-6000", "Transponder test set", "Available", "Avionics bay"),
        ],
      },
      {
        name: "Perform 23-90-20-7200 transponder functional certification",
        ref: "WO-20.47",
      },
    ],
  },
  {
    name: COMPASS_SWING,
    days: 1,
    offset: 13,
    shop: "Avionics",
    after: [],
    tasks: [
      {
        name: "Perform 053424B magnetic compass swing inspection",
        ref: "WO-20.42",
        tools: [item("MC-2", "Master compass", "Available", "Avionics bay")],
      },
    ],
  },
]
